import { inject, Injectable } from '@angular/core';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import * as LogRocket from 'logrocket';
import { appInjector } from '../../app-injector';
import { InitData, ModuleName } from '../../core-data-model';
import { ConfigurationService } from '../../system-configuration';
import { FeatureFlags, FeatureFlagsService } from '../FeatureFlags.service';
import { InitDataService } from '../InitData';
import { ModuleAccessService } from '../ModuleAccess';
import { ModuleUserAccessService } from '../ModuleUserAccess.service';
import { UserProfileSettingsService } from '../UserProfileSettings';

export interface LogRocketEventMetadata {
    [key: string]:
        | string
        | number
        | boolean
        | string[]
        | number[]
        | boolean[]
        | undefined;
}

export enum Module {
    Admin2,
    Design2,
    Diagnose2,
    Hire2,
    Inspire2,
    Platform,
    Spa,
    SurveyTaker,
    GroupAnalytics,
    SellerExperience
}

@Injectable({
    providedIn: 'root'
})
export class LogRocketService {
    private readonly config = inject(ConfigurationService);
    private readonly initDataService = inject(InitDataService);
    private readonly userProfile = inject(UserProfileSettingsService);
    private readonly moduleAccess = inject(ModuleAccessService);
    private readonly moduleUserAccess = inject(ModuleUserAccessService);

    /**
     * Call this method at app start to initialize LogRocket, initData has to be loaded before initialization
     * A custom 'Initialized' event is logged after LogRocket initialized is called
     *
     * @param moduleName ie Hire2, Inspire2, Design2, Diagnose2, Spa, NeoSpa etc
     */
    async initialize(moduleName: Module) {
        try {
            // intentionally obtained here, because of dependency graph issues if injected in the constructor
            const featureFlagService: FeatureFlagsService =
                appInjector().get(FeatureFlagsService);

            await featureFlagService.completionPromise;

            const enableLogRocket = featureFlagService.getByFlag(
                FeatureFlags.logRocketTestingEnabled
            );

            if (!enableLogRocket) {
                return;
            }
        } catch (error) {
            console.error(
                'LogRocket Failed to Initialize, check configuration',
                error
            );
            return;
        }

        if (!this.initDataService.isInitDataLoaded) {
            throw new Error(
                'JavaScript InitData must be loaded before initializing LogRocket'
            );
        }

        LogRocket.init('dzcoy1/pi-vyvd9', {
            // "If you're seeing multiple sessions created as users traverse different subdomains on
            // your site and you'd prefer to keep these contained to a single session, pass the
            // rootHostname option when calling LogRocket.init()."
            rootHostname: 'predictiveindex.com',
            network: {
                responseSanitizer: (response) => {
                    try {
                        const selectedCompanyGuid =
                            this.userProfile.getCurrentCompanySettings()
                                .companyId;

                        const hideForCompaniesList =
                            this.config.getCompanyGuidsWithMaskedCogData();

                        if (
                            hideForCompaniesList.includes(selectedCompanyGuid)
                        ) {
                            response.body = '**redacted**';
                        }
                    } catch {
                        console.error(
                            'Possible misconfiguration on hideForCompaniesList'
                        );
                        response.body = '**redacted**';
                    }

                    return response;
                }
            }
        });

        this.addSessionMetadata(this.initDataService.javaScriptInitData);
        this.pushInitializedEvent(moduleName);
    }

    /**
     * LogRocket custom events can be added to your app that allows you to track specific in-app behavior.
     * Standard eventNames TBD.
     *
     * @param eventName
     * @param metadata
     */
    pushCustomEvent(eventName: string, metadata?: LogRocketEventMetadata) {
        LogRocket.track(eventName, metadata);
    }

    private pushInitializedEvent(moduleName: Module) {
        const moduleString = Module[moduleName];
        this.pushCustomEvent('Initialized', { moduleName: moduleString });
    }

    private addSessionMetadata(initData: InitData) {
        // It appears this is a noop if LogRocket isn't initialized before this
        // This is actually good because we can just call this always and it'll only
        // do something in the case where we want to initialize LogRocket
        LogRocket.identify(initData.administrator.email, {
            userGuid: initData.administrator.userGuid,
            name: initData.administrator.fullName(),
            email: initData.administrator.email,

            // Add your own custom user variables here, ie:
            companyId: initData.administrator.companyId,
            role: initData.administrator.roleName,
            companyName: initData.administrator.company,
            softwareModuleAccessConfig: JSON.stringify(
                initData.softwareModuleAccessConfig
            ),
            allowedEmailDomains: JSON.stringify(
                this.initDataService.companyEmailDomains?.allowList
            ),
            vmAccessLevel: JSON.stringify(
                this.initDataService.userCompanyPermissionCollection
                    ?.vmAccessLevel
            ),
            moduleAccessLevel: JSON.stringify(
                this.initDataService.userCompanyPermissionCollection
                    ?.accessLevel
            ),
            PI2UpgradeStatus: initData.accountConfig.pi2UpgradeStatus,
            isBornInPI2: initData.accountConfig.isBornInPI2,
            piVersion: initData.accountConfig.piVersion,
            isThirdPartyUser:
                initData.userProfile.settings.currentCompany !==
                initData.administrator.companyId,
            accountClassification: this.moduleAccess.accountClassificationType,
            isOrgAdmin: this.moduleUserAccess.hasAccessLevelForAnyModule(
                PermissionsModuleAccessLevel.AllCompanyAdmin
            ),
            // Note: For all PI2 products be sure not to specify Modulename2
            // as that is not used in the permission endpiont being queried
            design2AccessLevel:
                PermissionsModuleAccessLevel[
                    this.moduleUserAccess.getUserAccessLevel(ModuleName.Design)
                ],
            hire2AccessLevel:
                PermissionsModuleAccessLevel[
                    this.moduleUserAccess.getUserAccessLevel(ModuleName.Hire)
                ],
            inspire2AccessLevel:
                PermissionsModuleAccessLevel[
                    this.moduleUserAccess.getUserAccessLevel(ModuleName.Inspire)
                ],
            diagnose2AccessLevel:
                PermissionsModuleAccessLevel[
                    this.moduleUserAccess.getUserAccessLevel(
                        ModuleName.Diagnose
                    )
                ]
        });
    }
}
